import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, query, where, orderBy, deleteDoc, writeBatch, runTransaction } from 'firebase/firestore';
import { custodyStatsOf } from '../pages/Custodies/custodyRules';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';
import logger from '../utils/logger';

const CUSTODIES_COLLECTION = 'custodies';
const EXPENSES_COLLECTION = 'expenses';
const SETTLEMENTS_COLLECTION = 'settlements';

// With the new store (USE_CONTRACTS_V2) petty cash is the engine's office records (/api/office/custodies...):
// every call goes to its route and the SERVER moves the custody's balance - an expense added, changed or deleted
// and the custody's spent / remaining change in one database transaction on the locked custody, from the amounts
// the server has stored (never from what this page last saw); a settlement settles the custody's figures as they
// are at that moment. A change refused because the record moved on answers {success: false, error} with
// error.code 'record-changed' (someone saved it since: reload) or 'invoice-exists' (the invoice number is taken),
// and error.message the engine's own words for the user.
//
// The custody versions of the last read (custody id -> _version): a settlement names the version whose spent /
// remaining the user confirmed, so it is refused when the custody moved on meanwhile.
const custodyVersions = new Map();
const rememberCustody = (c) => { if (c && c.id !== undefined && c._version !== undefined) custodyVersions.set(String(c.id), c._version); };
// (and each expense's, for a change whose page did not carry the expense's own _version)
const expenseVersions = new Map();
const refused = (e) => { throw officeError(e); };
// The fields an expense / settlement save sends: what the page typed, never the record's own keys or totals
const OWN_KEYS = ['id', '_version', 'version', 'custody_id', 'created_at', 'created_by', 'last_updated', 'updated_by'];
const withoutOwnKeys = (data) => Object.fromEntries(Object.entries(data || {}).filter(([k]) => !OWN_KEYS.includes(k)));

// The reads THROW on failure (a denied read included): the pages show an error state.
// They used to return [] / null, so a user whose role cannot read petty cash saw
// "Total Open Amount AED 0" and an empty list as if there were no custodies (audit ADM-18).
export const custodyService = {
    // --- CUSTODIES ---

    getAllCustodies: async () => {
        try {
            if (USE_CONTRACTS_V2) {
                const items = await finesService.officeCustodies().catch(refused);
                (items || []).forEach(rememberCustody);
                return items || [];
            }
            const q = query(collection(db, CUSTODIES_COLLECTION), orderBy('open_date', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            logger.error("Error fetching custodies:", error);
            throw error;
        }
    },

    /** The custody, or null when it does not exist (a failed read throws). */
    getCustodyById: async (id) => {
        try {
            if (USE_CONTRACTS_V2) {
                try {
                    const custody = await finesService.officeCustody(id);
                    rememberCustody(custody);
                    return custody;
                } catch (e) {
                    if (e?.response?.status === 404) return null;   // no such custody (or deleted): as a missing doc
                    throw officeError(e);
                }
            }
            const docRef = doc(db, CUSTODIES_COLLECTION, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
            }
            return null;
        } catch (error) {
            logger.error("Error fetching custody:", error);
            throw error;
        }
    },

    createCustody: async (data) => {
        try {
            if (USE_CONTRACTS_V2) {
                // opened with nothing spent by the server (created_at / created_by stamped there)
                const custody = await finesService.officeCustodyCreate({ ...data, total_amount: Number(data.total_amount) }).catch(refused);
                rememberCustody(custody);
                return { success: true };
            }
            const newCustody = {
                ...data,
                total_amount: Number(data.total_amount),
                spent: 0,
                remaining: Number(data.total_amount),
                status: 'open',
                created_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            };
            await addDoc(collection(db, CUSTODIES_COLLECTION), newCustody);
            return { success: true };
        } catch (error) {
            logger.error("Error creating custody:", error);
            return { success: false, error };
        }
    },

    deleteCustody: async (id) => {
        try {
            if (USE_CONTRACTS_V2) {
                // the custody leaves every list; the store keeps its row and its expenses (money records)
                await finesService.officeCustodyDelete(id).catch(refused);
                custodyVersions.delete(String(id));
                return { success: true };
            }
            await deleteDoc(doc(db, CUSTODIES_COLLECTION, id));
            return { success: true };
        } catch (error) {
            logger.error("Error deleting custody:", error);
            return { success: false, error };
        }
    },

    archiveCustody: async (id) => {
        try {
            if (USE_CONTRACTS_V2) {
                rememberCustody(await finesService.officeCustodyArchive(id).catch(refused));
                return { success: true };
            }
            const docRef = doc(db, CUSTODIES_COLLECTION, id);
            await updateDoc(docRef, {
                status: 'archived',
                archived_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error("Error archiving custody:", error);
            return { success: false, error };
        }
    },

    reopenCustody: async (id) => {
        try {
            if (USE_CONTRACTS_V2) {
                rememberCustody(await finesService.officeCustodyReopen(id).catch(refused));
                return { success: true };
            }
            const docRef = doc(db, CUSTODIES_COLLECTION, id);
            await updateDoc(docRef, {
                status: 'open',
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error("Error reopening custody:", error);
            return { success: false, error };
        }
    },

    // --- EXPENSES ---

    getExpensesByCustodyId: async (custodyId) => {
        try {
            // the store answers the legacy 'vendor' as invoice_number already
            if (USE_CONTRACTS_V2) {
                const items = (await finesService.officeExpenses(custodyId).catch(refused)) || [];
                items.forEach((e) => { if (e && e._version !== undefined) expenseVersions.set(String(e.id), e._version); });
                return items;
            }
            const expensesCollection = collection(db, EXPENSES_COLLECTION);
            const q = query(expensesCollection, where('custody_id', '==', custodyId), orderBy('expense_date', 'desc'));
            const snapshot = await getDocs(q);
            // Map legacy 'vendor' to 'invoice_number' if needed
            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    invoice_number: data.invoice_number || data.vendor || ''
                };
            });
        } catch (error) {
            logger.error("Error fetching expenses:", error);
            throw error;
        }
    },

    checkInvoiceNumberExists: async (invoiceNumber, excludeId = null) => {
        try {
            if (USE_CONTRACTS_V2) {
                // the form's early check; the server checks again under a lock when the expense is saved
                return !!(await finesService.officeExpensesByInvoice(invoiceNumber, excludeId))?.exists;
            }
            const expensesCollection = collection(db, EXPENSES_COLLECTION);
            // Check against 'invoice_number' field
            const q = query(expensesCollection, where('invoice_number', '==', invoiceNumber));
            const snapshot = await getDocs(q);

            // Also check legacy 'vendor' field just in case
            const qLegacy = query(expensesCollection, where('vendor', '==', invoiceNumber));
            const snapshotLegacy = await getDocs(qLegacy);

            const allDocs = [...snapshot.docs, ...snapshotLegacy.docs];

            // Filter out the current document if we are editing
            const exists = allDocs.some(doc => doc.id !== excludeId);
            return exists;
        } catch (error) {
            logger.error("Error checking invoice number:", error);
            return false;
        }
    },

    addExpense: async (custodyId, expenseData) => {
        try {
            if (USE_CONTRACTS_V2) {
                // the expense + the custody's spent / remaining in one transaction on the server
                const answer = await finesService.officeExpenseAdd(custodyId, {
                    ...withoutOwnKeys(expenseData),
                    amount: Number(expenseData.amount),
                }).catch(refused);
                rememberCustody(answer?.custody);
                return { success: true };
            }
            const expenseRef = doc(collection(db, EXPENSES_COLLECTION));
            const custodyRef = doc(db, CUSTODIES_COLLECTION, custodyId);
            const userEmail = auth.currentUser?.email || '';

            await runTransaction(db, async (transaction) => {
                // 1. Read current custody totals inside the transaction
                const custodySnap = await transaction.get(custodyRef);
                if (!custodySnap.exists()) throw new Error("Custody not found");
                const custodyData = custodySnap.data();

                const newSpent = (Number(custodyData.spent) || 0) + Number(expenseData.amount);
                const newRemaining = (Number(custodyData.total_amount) || 0) - newSpent;

                // 2. Add Expense
                const expensePayload = {
                    ...expenseData,
                    custody_id: custodyId,
                    amount: Number(expenseData.amount),
                    created_at: new Date().toISOString(),
                    last_updated: new Date().toISOString(),
                    invoice_number: expenseData.invoice_number, // explicit save
                    created_by: userEmail
                };
                transaction.set(expenseRef, expensePayload);

                // 3. Update Custody Totals
                transaction.update(custodyRef, {
                    spent: newSpent,
                    remaining: newRemaining,
                    last_updated: new Date().toISOString(),
                    updated_by: userEmail
                });
            });

            return { success: true };
        } catch (error) {
            logger.error("Error adding expense:", error);
            return { success: false, error };
        }
    },

    /**
     * `oldAmount`: Firestore only. On the store the balance moves by the new amount minus the STORED one, and the
     * save names the version the expense was opened with (`expenseData._version`, as the list read gave it).
     */
    updateExpense: async (custodyId, expenseId, expenseData, oldAmount) => {
        try {
            if (USE_CONTRACTS_V2) {
                const answer = await finesService.officeExpenseUpdate(custodyId, expenseId, {
                    ...withoutOwnKeys(expenseData),
                    amount: Number(expenseData.amount),
                }, expenseData?._version ?? expenseVersions.get(String(expenseId))).catch(refused);
                rememberCustody(answer?.custody);
                if (answer?.expense?._version !== undefined) expenseVersions.set(String(expenseId), answer.expense._version);
                return { success: true };
            }
            const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
            const custodyRef = doc(db, CUSTODIES_COLLECTION, custodyId);
            const userEmail = auth.currentUser?.email || '';

            await runTransaction(db, async (transaction) => {
                // 1. Read current custody totals inside the transaction
                const custodySnap = await transaction.get(custodyRef);
                if (!custodySnap.exists()) throw new Error("Custody not found");
                const currentData = custodySnap.data();

                const newSpent = (Number(currentData.spent) || 0) - Number(oldAmount) + Number(expenseData.amount);
                const newRemaining = (Number(currentData.total_amount) || 0) - newSpent;

                // 2. Update Expense
                transaction.update(expenseRef, {
                    ...expenseData,
                    amount: Number(expenseData.amount),
                    invoice_number: expenseData.invoice_number,
                    last_updated: new Date().toISOString(),
                    updated_by: userEmail
                });

                // 3. Update Custody Totals
                transaction.update(custodyRef, {
                    spent: newSpent,
                    remaining: newRemaining,
                    last_updated: new Date().toISOString(),
                    updated_by: userEmail
                });
            });

            return { success: true };
        } catch (error) {
            logger.error("Error updating expense:", error);
            return { success: false, error };
        }
    },

    /** `amount`: Firestore only - on the store the STORED amount leaves the custody's spent. */
    deleteExpense: async (custodyId, expenseId, amount) => {
        try {
            if (USE_CONTRACTS_V2) {
                const answer = await finesService.officeExpenseDelete(custodyId, expenseId).catch(refused);
                rememberCustody(answer?.custody);
                return { success: true };
            }
            const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
            const custodyRef = doc(db, CUSTODIES_COLLECTION, custodyId);
            const userEmail = auth.currentUser?.email || '';

            await runTransaction(db, async (transaction) => {
                // 1. Read current custody totals inside the transaction
                const custodySnap = await transaction.get(custodyRef);

                // 2. Update Custody Totals (Reverse the amount)
                if (custodySnap.exists()) {
                    const currentData = custodySnap.data();
                    const newSpent = (Number(currentData.spent) || 0) - Number(amount);
                    const newRemaining = (Number(currentData.total_amount) || 0) - newSpent;

                    transaction.update(custodyRef, {
                        spent: newSpent,
                        remaining: newRemaining,
                        last_updated: new Date().toISOString(),
                        updated_by: userEmail
                    });
                }

                // 3. Delete Expense
                transaction.delete(expenseRef);
            });

            return { success: true };
        } catch (error) {
            logger.error("Error deleting expense:", error);
            return { success: false, error };
        }
    },

    // --- SETTLEMENTS ---

    getSettlementsByCustodyId: async (custodyId) => {
        try {
            if (USE_CONTRACTS_V2) return (await finesService.officeSettlements(custodyId).catch(refused)) || [];
            const settlementsCollection = collection(db, SETTLEMENTS_COLLECTION);
            const q = query(settlementsCollection, where('custody_id', '==', custodyId), orderBy('settlement_date', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            logger.error("Error fetching settlements:", error);
            throw error;
        }
    },

    submitSettlement: async (custodyId, settlementData) => {
        try {
            if (USE_CONTRACTS_V2) {
                // the server settles the custody's spent / remaining as they are now (settled_amount /
                // difference are its figures, not the page's) - refused when the custody moved on after the user
                // saw the figures (the version of the last read)
                const { settled_amount: _settled, difference: _difference, ...rest } = withoutOwnKeys(settlementData);
                const answer = await finesService.officeSettlementAdd(custodyId, rest, custodyVersions.get(String(custodyId))).catch(refused);
                rememberCustody(answer?.custody);
                return { success: true };
            }
            const batch = writeBatch(db);
            const settlementRef = doc(collection(db, SETTLEMENTS_COLLECTION));
            const custodyRef = doc(db, CUSTODIES_COLLECTION, custodyId);

            // 1. Create Settlement Record
            batch.set(settlementRef, {
                ...settlementData,
                custody_id: custodyId,
                created_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            });

            // 2. Update Custody Status
            batch.update(custodyRef, {
                status: 'settled', // or 'settlement_submitted'
                closed_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            });

            await batch.commit();
            return { success: true };
        } catch (error) {
            logger.error("Error submitting settlement:", error);
            return { success: false, error };
        }
    },

    // --- DASHBOARD ---

    findCustodyIdsByInvoice: async (invoiceNumber) => {
        try {
            if (USE_CONTRACTS_V2) {
                const found = await finesService.officeExpensesByInvoice(invoiceNumber);
                return { ids: found?.ids || [], expenses: found?.expenses || [] };
            }
            const expensesRef = collection(db, EXPENSES_COLLECTION);
            const q = query(expensesRef, where('invoice_number', '==', invoiceNumber));
            const snapshot = await getDocs(q);

            // Also check 'vendor' for legacy support
            const qLegacy = query(expensesRef, where('vendor', '==', invoiceNumber));
            const snapshotLegacy = await getDocs(qLegacy);

            const allDocs = [...snapshot.docs, ...snapshotLegacy.docs];

            const expenses = allDocs.map(d => ({ id: d.id, ...d.data() }));
            const custodyIds = [...new Set(expenses.map(e => e.custody_id))];

            return { ids: custodyIds, expenses };
        } catch (error) {
            logger.error("Error searching by invoice:", error);
            return { ids: [], expenses: [] };
        }
    },

    /**
     * Dashboard tiles. Pass the list already read to avoid a second read of the whole
     * collection; a failed read throws (it used to return zeros - ADM-18).
     */
    getGlobalStats: async (custodies = null) => custodyStatsOf(custodies || await custodyService.getAllCustodies())
};
